import { AnyAction, createSlice, SliceCaseReducers } from '@reduxjs/toolkit';
import * as actionTypes from './tunnelActionTypes';
import {
  FixMe,
  RootState,
  TunnelConfiguration,
  TunnelState,
  TunnelVersion,
} from '@greenkub-fronts/shared/interface';
import persistConfigurationsUseCase
  from './useCases/persistConfigurations/persit-configurations.use-case';

export const TUNNEL_FEATURE_KEY = 'tunnel';

const tunnelReducerInit = (): TunnelState => {
  return {
    version: TunnelVersion.R1,
    currentProject: null,
    currentConfigIndex: 0,
    errorModalIsOpen: false,
    missingWindowColorModalIsOpen: false,
    haveBeenTouched: false,
    lastPersistStatus: 'success',
    configurations: [
      {
        quantity: 1,
        currentSurface: 25,
        currentModel: null,
        currentFinish: null,
        currentEquipment: null,
        currentOptions: [],
        currentCustomProduct: [],
        currentNegativeDefaultOptionModel: [],
        currentNegativeDefaultOptionFinishEquipment: [],
      },
    ],
  };
};

export const initialTunnelState: TunnelState = tunnelReducerInit();

const tunnelSlice = createSlice<TunnelState, SliceCaseReducers<TunnelState>, string>(
  {
    name: TUNNEL_FEATURE_KEY,
    initialState: initialTunnelState,
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(actionTypes.INIT_TUNNEL, (state, action: AnyAction) => {
        state.version = action['payload'].version;
        state.currentProject = action['payload'].project;
        state.configurations = action['payload'].configurations;
      });
      builder.addCase(actionTypes.SET_MODEL, (state, action: AnyAction) => {
        state.configurations[state.currentConfigIndex].currentModel =
          action['payload'].model;
        state.configurations[state.currentConfigIndex].currentSurface = parseInt(
          action['payload'].surface,
        );
        state.haveBeenTouched = true;
      });
      builder.addCase(actionTypes.SET_KIT, (state, action: AnyAction) => {
        state.configurations[state.currentConfigIndex].currentModel =
          action['payload'].kit;
        state.configurations[state.currentConfigIndex].currentFinish =
          action['payload'].finishAccess;
        state.configurations[state.currentConfigIndex].currentEquipment =
          action['payload'].equipmentVide;
        const quantity = state.configurations[state.currentConfigIndex].quantity;
        state.configurations[state.currentConfigIndex].quantity =
          quantity > 0 ? quantity : 1;
        state.haveBeenTouched = true;
      });
      builder.addCase(actionTypes.SET_FINISH, (state, action: AnyAction) => {
        state.configurations[state.currentConfigIndex].currentFinish =
          action['payload'].finish;
        state.configurations[state.currentConfigIndex].currentOptions =
          action['payload'].selectedOptions;
        state.haveBeenTouched = true;
      });
      builder.addCase(actionTypes.SET_EQUIPMENT, (state, action: AnyAction) => {
        state.configurations[state.currentConfigIndex].currentEquipment =
          action['payload'].equipment;
        state.haveBeenTouched = true;
      });
      builder.addCase(actionTypes.SET_OPTIONS, (state, action: AnyAction) => {
        state.configurations[state.currentConfigIndex].currentOptions =
          action['payload'].options;
        state.configurations[state.currentConfigIndex].currentCustomProduct =
          action['payload'].customProducts;
        state.missingWindowColorModalIsOpen =
          action['payload'].missingWindowColorModalIsOpen;
        state.haveBeenTouched = true;
      });
      builder.addCase(actionTypes.SET_OPTIONS_FOR_CONFIG,
        (state, action: AnyAction) => {
          state.configurations[action['payload'].configIndex].currentOptions =
            action['payload'].options;
          state.haveBeenTouched = true;
        });
      builder.addCase(
        actionTypes.SET_NEGATIVE_DEFAULT_OPTIONS_MODELS,
        (state, action: AnyAction) => {
          state.configurations[
            state.currentConfigIndex
            ].currentNegativeDefaultOptionModel =
            action['payload'].negativeDefaultOptionModels;
        },
      );
      builder.addCase(
        actionTypes.SET_NEGATIVE_DEFAULT_OPTIONS_FINISH_EQUIPMENT,
        (state, action: AnyAction) => {
          state.configurations[
            state.currentConfigIndex
            ].currentNegativeDefaultOptionFinishEquipment =
            action['payload'].negativeDefaultOptionFinishEquipment;
        },
      );
      builder.addCase(
        actionTypes.ADD_NEW_CONFIGURATION,
        (state, action: AnyAction) => {
          const newIndex = state.configurations.length;
          state.currentConfigIndex = newIndex;
          state.configurations[newIndex] = initialTunnelState.configurations[0] as FixMe;
        },
      );
      builder.addCase(
        actionTypes.CHANGE_CURRENT_CONFIGURATION,
        (state, action: AnyAction) => {
          state.currentConfigIndex = action['payload'].newIndex;
        },
      );
      builder.addCase(
        persistConfigurationsUseCase.fulfilled,
        (state, action: AnyAction) => {
          state.haveBeenTouched = false;
          if (action['payload'].status !== 'fail') {
            state.currentProject = action['payload'].project;
          }
          state.lastPersistStatus = action['payload'].status;
          state.errorModalIsOpen = action['payload'].errorModalIsOpen;
        },
      );
      builder.addCase(
        persistConfigurationsUseCase.rejected,
        (state, action: AnyAction) => {
          state.lastPersistStatus = action['payload'].status;
          state.errorModalIsOpen = action['payload'].errorModalIsOpen;
        },
      );
      builder.addCase(
        actionTypes.DELETE_CONFIGURATION,
        (state, action: AnyAction) => {
          state.configurations = action['payload'].configurations;
        },
      );
      builder.addCase(actionTypes.INCREMENT_MODEL,
        (state, action: AnyAction) => {
          const quantity =
            state.configurations[action['payload'].configIdx].quantity;
          state.configurations[action['payload'].configIdx].quantity =
            quantity > 0 ? quantity + 1 : 1;
        });
      builder.addCase(actionTypes.DECREMENT_MODEL,
        (state, action: AnyAction) => {
          const newQuantity =
            state.configurations[action['payload'].configIdx].quantity - 1;
          state.configurations[action['payload'].configIdx].quantity =
            newQuantity > 0 ? newQuantity : 1;
        });
      builder.addCase(actionTypes.CLOSE_ERROR_MODAL, (state) => {
        state.errorModalIsOpen = false;
      });
      builder.addCase(actionTypes.CLOSE_MISSING_WINDOW_COLOR_MODAL, (state) => {
        state.missingWindowColorModalIsOpen = false;
      });
    },
  });

export const tunnel = tunnelSlice.reducer;

export const getCurrentConfiguration = (rootState: RootState): TunnelConfiguration => {
  return rootState[TUNNEL_FEATURE_KEY].configurations[
    rootState[TUNNEL_FEATURE_KEY].currentConfigIndex
    ];
};
